import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCredits, CreditTransaction } from '@/hooks/useCredits';
import { useToast } from '@/components/ui/use-toast';
import { format, subDays, startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';
import { Download, Copy, Calendar, TrendingUp, BarChart3, Activity } from 'lucide-react';

// Color mapping for different categories
const CATEGORY_COLORS = {
  content_generation: '#3b82f6', // blue
  image_processing: '#10b981',   // emerald
  video_processing: '#f59e0b',   // amber
  other: '#8b5cf6',             // violet
  api: '#ef4444',               // red
  features: '#06b6d4'           // cyan
} as const;

interface UsageDataPoint {
  date: string;
  formattedDate: string;
  content_generation: number;
  image_processing: number;
  video_processing: number;
  other: number;
  api: number;
  features: number;
  total: number;
  cumulative: number;
}

type TimeRange = '7' | '30' | '90' | 'all';

/**
 * CreditUsageAnalytics component - Displays credit usage analytics with charts and export functionality
 * 
 * Features:
 * - Daily usage chart showing credit consumption by category over time
 * - Time range selector (7, 30, 90 days, All time)
 * - Interactive tooltips with exact values
 * - CSV export functionality
 * - One-click clipboard copy
 * - Responsive design for mobile viewing
 */
export function CreditUsageAnalytics() {
  const { fetchTransactions } = useCredits();
  const { toast } = useToast();
  const [timeRange, setTimeRange] = useState<TimeRange>('30');
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    const now = new Date();
    const endDate = endOfDay(now);
    let startDate: Date;

    switch (timeRange) {
      case '7':
        startDate = startOfDay(subDays(now, 7));
        break;
      case '30':
        startDate = startOfDay(subDays(now, 30));
        break;
      case '90':
        startDate = startOfDay(subDays(now, 90));
        break;
      case 'all':
      default:
        startDate = new Date('2020-01-01'); // Far enough back to include all data
        break;
    }

    return { startDate, endDate };
  }, [timeRange]);

  // Fetch transactions for the selected time range
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTransactions({
        limit: 1000, // Get more records for analytics
        offset: 0,
        type: 'consumption', // Only consumption transactions for usage analytics
        startDate: timeRange !== 'all' ? dateRange.startDate.toISOString() : undefined,
        endDate: timeRange !== 'all' ? dateRange.endDate.toISOString() : undefined,
      });
      
      setTransactions(result.transactions || []);
    } catch (err) {
      console.error('Failed to load transactions for analytics:', err);
      setError('Failed to load transaction data');
    } finally {
      setLoading(false);
    }
  }, [fetchTransactions, timeRange, dateRange]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Process transactions into chart data
  const chartData = useMemo((): UsageDataPoint[] => {
    if (!transactions.length) return [];

    // Filter transactions by date range
    const filteredTransactions = transactions.filter(t => {
      const transactionDate = new Date(t.created_at);
      return isWithinInterval(transactionDate, {
        start: dateRange.startDate,
        end: dateRange.endDate
      });
    });

    // Group transactions by date
    const dateGroups = new Map<string, CreditTransaction[]>();
    
    filteredTransactions.forEach(transaction => {
      const dateKey = format(new Date(transaction.created_at), 'yyyy-MM-dd');
      if (!dateGroups.has(dateKey)) {
        dateGroups.set(dateKey, []);
      }
      dateGroups.get(dateKey)!.push(transaction);
    });

    // Create data points for each date
    const dataPoints: UsageDataPoint[] = [];
    const sortedDates = Array.from(dateGroups.keys()).sort();
    let cumulativeTotal = 0;

    sortedDates.forEach(dateKey => {
      const dayTransactions = dateGroups.get(dateKey)!;
      
      // Aggregate by category
      const categoryCounts = {
        content_generation: 0,
        image_processing: 0,
        video_processing: 0,
        other: 0,
        api: 0,
        features: 0
      };

      let dayTotal = 0;

      dayTransactions.forEach(transaction => {
        const credits = Math.abs(transaction.amount); // Use absolute value since consumption is negative
        const category = transaction.category || 'other';
        
        if (category in categoryCounts) {
          categoryCounts[category as keyof typeof categoryCounts] += credits;
        } else {
          categoryCounts.other += credits;
        }
        
        dayTotal += credits;
      });

      cumulativeTotal += dayTotal;

      dataPoints.push({
        date: dateKey,
        formattedDate: format(new Date(dateKey), 'MMM d'),
        ...categoryCounts,
        total: dayTotal,
        cumulative: cumulativeTotal
      });
    });

    return dataPoints;
  }, [transactions, dateRange]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as UsageDataPoint;
      
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium">{format(new Date(data.date), 'MMM d, yyyy')}</p>
          <div className="mt-2 space-y-1">
            {Object.entries(CATEGORY_COLORS).map(([category, color]) => {
              const value = data[category as keyof typeof CATEGORY_COLORS];
              if (value > 0) {
                return (
                  <div key={category} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm capitalize">
                        {category.replace('_', ' ')}
                      </span>
                    </div>
                    <span className="text-sm font-medium">{value.toLocaleString()}</span>
                  </div>
                );
              }
              return null;
            })}
            <div className="border-t pt-1 mt-2">
              <div className="flex justify-between font-medium">
                <span>Total:</span>
                <span>{data.total.toLocaleString()} credits</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Generate CSV data for export
  const generateCSVData = useCallback(() => {
    const headers = [
      'Date',
      'Content Generation',
      'Image Processing', 
      'Video Processing',
      'API',
      'Features',
      'Other',
      'Daily Total',
      'Cumulative Total'
    ];

    const rows = chartData.map(dataPoint => [
      dataPoint.date,
      dataPoint.content_generation.toString(),
      dataPoint.image_processing.toString(),
      dataPoint.video_processing.toString(),
      dataPoint.api.toString(),
      dataPoint.features.toString(),
      dataPoint.other.toString(),
      dataPoint.total.toString(),
      dataPoint.cumulative.toString()
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }, [chartData]);

  // Export as CSV file
  const exportAsCSV = useCallback(() => {
    try {
      const csvContent = generateCSVData();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `credit-usage-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Export successful",
        description: "Usage analytics data exported to CSV file",
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: "Export failed",
        description: "Failed to export usage data",
        variant: "destructive"
      });
    }
  }, [generateCSVData, toast]);

  // Copy CSV data to clipboard
  const copyToClipboard = useCallback(async () => {
    try {
      const csvContent = generateCSVData();
      await navigator.clipboard.writeText(csvContent);
      
      toast({
        title: "Copied to clipboard",
        description: "Usage analytics data copied as CSV format",
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy data to clipboard",
        variant: "destructive"
      });
    }
  }, [generateCSVData, toast]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalCredits = chartData.reduce((sum, day) => sum + day.total, 0);
    const avgDaily = chartData.length > 0 ? totalCredits / chartData.length : 0;
    const peakDay = chartData.reduce((peak, day) => day.total > peak.total ? day : peak, { total: 0, date: '' });
    
    return {
      totalCredits,
      avgDaily,
      peakDay,
      daysWithUsage: chartData.filter(day => day.total > 0).length
    };
  }, [chartData]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center text-muted-foreground">
            Loading usage analytics...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center text-destructive">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200/50 hover:shadow-md transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{summaryStats.totalCredits.toLocaleString()}</div>
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Total Credits Used</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200/50 hover:shadow-md transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">{Math.round(summaryStats.avgDaily).toLocaleString()}</div>
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">Avg Daily Usage</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-orange-200/50 hover:shadow-md transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">{summaryStats.peakDay.total.toLocaleString()}</div>
                <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Peak Day Usage</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                <Activity className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200/50 hover:shadow-md transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">{summaryStats.daysWithUsage}</div>
                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Active Days</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center">
                <Calendar className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Main Chart */}
      <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="p-6 border-b bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-cyan-900 dark:text-cyan-100 mb-1">
                  Usage Analytics
                </h3>
                <p className="text-sm text-cyan-700 dark:text-cyan-200">
                  Credit consumption over time by category with interactive controls
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={timeRange} onValueChange={(value: TimeRange) => setTimeRange(value)}>
                <SelectTrigger className="w-[180px] bg-white/80 backdrop-blur-sm border-cyan-200 hover:border-cyan-300 transition-colors">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyToClipboard}
                  disabled={chartData.length === 0}
                  className="bg-white/80 backdrop-blur-sm border-cyan-200 hover:bg-cyan-50 hover:border-cyan-300 transition-all duration-200"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={exportAsCSV}
                  disabled={chartData.length === 0}
                  className="bg-white/80 backdrop-blur-sm border-cyan-200 hover:bg-cyan-50 hover:border-cyan-300 transition-all duration-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
        </div>
        <CardContent className="p-6">
          {chartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">No usage data available</h3>
              <p className="text-muted-foreground text-center max-w-sm">No usage data found for the selected time range. Try adjusting your time range or check back later.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4">
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{
                        top: 10,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <defs>
                        {Object.entries(CATEGORY_COLORS).map(([category, color]) => (
                          <linearGradient key={category} id={`gradient-${category}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.9}/>
                            <stop offset="95%" stopColor={color} stopOpacity={0.2}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="formattedDate" 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="rect"
                        iconSize={12}
                      />
                      
                      {Object.entries(CATEGORY_COLORS).map(([category, color]) => (
                        <Area
                          key={category}
                          type="monotone"
                          dataKey={category}
                          stackId="1"
                          stroke={color}
                          fill={`url(#gradient-${category})`}
                          strokeWidth={2}
                          name={category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </CardContent>
        <div className="h-2 bg-gradient-to-r from-cyan-200 via-teal-200 to-emerald-200 dark:from-cyan-800 dark:via-teal-800 dark:to-emerald-800" />
      </Card>
    </div>
  );
}